
import React, { useState, useEffect } from 'react';
import type { View, DrugInfo, AnswerLength, DosingInfo, AdverseReactionInfo, PharmacologyInfo } from '../types';
import { getDrugById } from '../services/pharmacyService';
import AiDisclaimer from './AiDisclaimer';
import { useAuth } from '../contexts/AuthContext';
import { pushClinicianDataToCloud } from '../services/userSyncService';
import SEO from './SEO';

// --- ICONS ---
const BackIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>;
const StarIcon: React.FC<{ filled: boolean; className?: string }> = ({ filled, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 transition-colors ${className} ${filled ? 'text-yellow-400 fill-current' : 'text-gray-500 hover:text-yellow-400'}`} viewBox="0 0 20 20" fill="currentColor">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
);
const ChatIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor"><path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm14 1a1 1 0 10-2 0v2a1 1 0 102 0V6zM5 8a1 1 0 100-2 1 1 0 000 2z" /></svg>;
const CalculatorIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>;
const InteractionsIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>;


type Tab = 'Overview' | 'Dosing' | 'Safety' | 'Pharmacology';
const TABS: Tab[] = ['Overview', 'Dosing', 'Safety', 'Pharmacology'];

interface DrugDetailProps {
    drugId: string;
    onNavigate: (view: View) => void;
    onSendMessage: (message: string, answerLength: AnswerLength) => void;
    setActiveTool: (tool: string | null) => void;
}

const DrugDetail: React.FC<DrugDetailProps> = ({ drugId, onNavigate, onSendMessage, setActiveTool }) => {
    const { uid } = useAuth();
    const [drug, setDrug] = useState<DrugInfo | null>(null);
    const [isFavorite, setIsFavorite] = useState(false);
    const [activeTab, setActiveTab] = useState<Tab>('Overview');

    useEffect(() => {
        if (!uid) return;
        const fetchDrug = async () => {
            const data = await getDrugById(drugId, uid);
            setDrug(data);
        };
        fetchDrug();
        const storedFavorites = JSON.parse(localStorage.getItem(`drugFavorites_${uid}`) || '[]');
        setIsFavorite(storedFavorites.includes(drugId));
    }, [drugId, uid]);
    
    const handleToggleFavorite = () => {
        if (!uid) return;
        const storedFavorites = JSON.parse(localStorage.getItem(`drugFavorites_${uid}`) || '[]');
        const newFavorites = isFavorite ? storedFavorites.filter((id: string) => id !== drugId) : [...storedFavorites, drugId];
        localStorage.setItem(`drugFavorites_${uid}`, JSON.stringify(newFavorites));
        pushClinicianDataToCloud(uid, { favorites: newFavorites });
        setIsFavorite(!isFavorite);
    };
    
    if (!drug) {
        return <div className="text-center py-20">Loading drug information...</div>;
    }

    // Create JSON-LD Schema for MedicalDrug
    const drugSchema = {
        "@context": "https://schema.org",
        "@type": "Drug",
        "name": drug.brandName,
        "proprietaryName": drug.brandName,
        "nonProprietaryName": drug.genericName,
        "drugClass": {
            "@type": "DrugClass",
            "name": drug.therapeuticClass
        },
        "warning": drug.hasBlackBoxWarning ? drug.blackBoxWarning : undefined,
        "manufacturer": {
            "@type": "Organization",
            "name": drug.manufacturer
        }
    };

    const renderDosing = () => {
        const hasRenalNote = typeof drug.dosingAndAdministration === 'string' 
            ? drug.dosingAndAdministration.toLowerCase().includes('renal')
            : (drug.dosingAndAdministration || []).some(d => d.notes?.toLowerCase().includes('renal'));

        return (
            <div className="space-y-6">
                {Array.isArray(drug.dosingAndAdministration) ? (
                    <div className="overflow-x-auto rounded-lg border border-blue-900/50">
                        <table className="w-full text-sm">
                            <thead className="bg-[#1E2E4A]"><tr className="text-left"><th className="p-3 font-semibold text-yellow-400">Indication</th><th className="p-3 font-semibold text-yellow-400">Population</th><th className="p-3 font-semibold text-yellow-400">Dose</th></tr></thead>
                            <tbody>
                                {(drug.dosingAndAdministration as DosingInfo[]).map((dose, i) => (
                                    <tr key={i} className="border-t border-blue-900/50"><td className="p-3 align-top font-semibold">{dose.indication}</td><td className="p-3 align-top">{dose.population}</td><td className="p-3 align-top">{dose.dose}{dose.notes && <p className="text-xs text-gray-400 mt-1 italic">{dose.notes}</p>}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : <p className="text-gray-300">{drug.dosingAndAdministration || 'N/A'}</p>}
                {hasRenalNote && <button onClick={() => setActiveTool('Pharmacokinetics')} className="inline-flex items-center gap-2 bg-blue-800/80 hover:bg-blue-700/80 text-white font-semibold py-2 px-4 rounded-lg transition-colors"><CalculatorIcon /> Calculate CrCl</button>}
            </div>
        );
    };

    const renderSafety = () => (
        <div className="space-y-6">
            {drug.hasBlackBoxWarning && <div className="bg-red-900/40 border-2 border-red-500/80 rounded-xl p-6"><h3 className="text-xl font-bold text-red-200 mb-2">Black Box Warning</h3><p className="text-gray-300">{drug.blackBoxWarning}</p></div>}
            <div><h4 className="text-lg font-bold text-white mb-3">Adverse Reactions</h4>
                {Array.isArray(drug.adverseReactions) ? (
                    (drug.adverseReactions as AdverseReactionInfo[]).map((ar, i) => <div key={i} className="mb-2"><p className="font-semibold text-yellow-300">{ar.frequency}</p><p className="text-gray-300">{ar.reactions}</p></div>)
                ) : <p className="text-gray-300">{drug.adverseReactions || 'N/A'}</p>}
            </div>
             <div><h4 className="text-lg font-bold text-white mb-3">Contraindications</h4><p className="text-gray-300">{drug.contraindications || 'N/A'}</p></div>
             <div><h4 className="text-lg font-bold text-white mb-3">Drug Interactions</h4><p className="text-gray-300">{drug.drugInteractions || 'N/A'}</p><button onClick={() => setActiveTool('Interactions')} className="mt-4 inline-flex items-center gap-2 bg-blue-800/80 hover:bg-blue-700/80 text-white font-semibold py-2 px-4 rounded-lg transition-colors"><InteractionsIcon /> Check Interactions</button></div>
        </div>
    );

    const renderPharmacology = () => {
        const pharm = drug.pharmacology;
        if (typeof pharm === 'object' && pharm !== null) {
            return (
                <div className="space-y-6">
                    <div><h4 className="text-lg font-bold text-white mb-2">Mechanism of Action</h4><p className="text-gray-300">{pharm.mechanismOfAction}</p></div>
                    {pharm.pharmacokinetics && (<div><h4 className="text-lg font-bold text-white mb-2">Pharmacokinetics</h4><div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="bg-black/20 p-3 rounded-md"><p className="font-semibold text-yellow-300">Absorption</p><p className="text-gray-400">{pharm.pharmacokinetics.absorption}</p></div>
                        <div className="bg-black/20 p-3 rounded-md"><p className="font-semibold text-yellow-300">Distribution</p><p className="text-gray-400">{pharm.pharmacokinetics.distribution}</p></div>
                        <div className="bg-black/20 p-3 rounded-md"><p className="font-semibold text-yellow-300">Metabolism</p><p className="text-gray-400">{pharm.pharmacokinetics.metabolism}</p></div>
                        <div className="bg-black/20 p-3 rounded-md"><p className="font-semibold text-yellow-300">Excretion</p><p className="text-gray-400">{pharm.pharmacokinetics.excretion}</p></div>
                    </div></div>)}
                </div>
            );
        }
        return <p className="text-gray-300">{typeof pharm === 'string' ? pharm : 'N/A'}</p>;
    }

    const renderOverview = () => (
        <div className="bg-black/20 p-6 rounded-lg border border-blue-900/50">
            <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="font-semibold text-gray-400">Therapeutic Class</p><p className="font-semibold text-white">{drug.therapeuticClass}</p></div>
                <div><p className="font-semibold text-gray-400">Manufacturer</p><p className="font-semibold text-white">{drug.manufacturer}</p></div>
                <div><p className="font-semibold text-gray-400">Dosage Forms</p><p className="font-semibold text-white">{drug.dosageForm}</p></div>
                <div><p className="font-semibold text-gray-400">Strengths</p><p className="font-semibold text-white">{drug.strength}</p></div>
            </div>
        </div>
    );

    const renderTabContent = () => {
        switch(activeTab) {
            case 'Overview': return renderOverview();
            case 'Dosing': return renderDosing();
            case 'Safety': return renderSafety();
            case 'Pharmacology': return renderPharmacology();
            default: return null;
        }
    };
    
    return (
        <div className="py-6">
            <SEO 
                title={`${drug.brandName} (${drug.genericName}) - Dr.Rx Drug Reference`}
                description={`Clinical information for ${drug.brandName} (${drug.genericName}) including dosing, side effects, contraindications, and pharmacology.`}
                schema={drugSchema}
                keywords={[drug.brandName, drug.genericName, drug.therapeuticClass, "Dosing", "Side Effects", "Pharmacology"]}
                url={`/?view=drug-detail&id=${drug.id}`}
            />

            <header className="mb-6">
                <button onClick={() => onNavigate('drug-index')} className="flex items-center text-sm text-yellow-400 hover:text-yellow-300 font-semibold mb-4"><BackIcon /> Back to Drug Index</button>
                <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-4xl font-bold text-white">{drug.brandName}<sup className="text-lg -top-4">®</sup></h1>
                        <h2 className="text-2xl text-gray-400">{drug.genericName}</h2>
                    </div>
                     <button onClick={handleToggleFavorite} className="p-2" aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}><StarIcon filled={isFavorite} /></button>
                </div>
            </header>
            
            <div className="sticky top-0 bg-[#101D35]/90 backdrop-blur-lg z-10 py-2 mb-6 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 border-y border-blue-900/50">
                <div className="flex items-center space-x-2 overflow-x-auto">
                    {TABS.map(tab => (
                        <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 rounded-full font-semibold transition-colors duration-200 text-sm flex-shrink-0 ${activeTab === tab ? 'bg-yellow-400 text-gray-900' : 'text-gray-300 hover:bg-blue-800/50'}`} role="tab" aria-selected={activeTab === tab}>{tab}</button>
                    ))}
                </div>
            </div>
            
            <main className="tab-content-enter">
                {renderTabContent()}
                <div className="mt-8 pt-6 border-t border-blue-900/50">
                    <button onClick={() => onSendMessage(`Tell me more about ${drug.brandName}`, 'short')} className="w-full bg-blue-800/80 hover:bg-blue-700/80 text-white font-semibold py-3 px-4 rounded-lg flex items-center justify-center transition-colors"><ChatIcon/> Ask AI About {drug.brandName}</button>
                    <div className="mt-4"><AiDisclaimer /></div>
                </div>
            </main>
        </div>
    );
};

export default DrugDetail;